/**
 * 时间盒工作法动画
 * Timeboxing Animation
 *
 * 核心效果：
 * 1. 时间轴展示一天的时间分配
 * 2. 不同类型的时间盒色块
 * 3. 番茄钟倒计时动画
 * 4. 深度工作/休息交替可视化
 * 5. 效率统计图表
 *
 * 视觉规范：
 * - 背景：深黑 #0A0E27
 * - 深度工作：紫色 #8B5CF6
 * - 番茄钟：红色 #EF4444
 * - 休息：绿色 #10B981
 * - 会议：蓝色 #3B82F6
 * - 文字大小：≥48px
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ==================== 类型定义 ====================

type TimeBlock = {
  id: string;
  type: 'deep-work' | 'pomodoro' | 'break' | 'meeting' | 'admin';
  label: string;
  duration: number; // 分钟
  startHour: number;
  color: string;
  icon: string;
};

type TimeboxingAnimationProps = {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
};

// ==================== 数据配置 ====================

const TIME_BLOCKS: TimeBlock[] = [
  { id: '1', type: 'deep-work', label: '深度工作', duration: 90, startHour: 9, color: '#8B5CF6', icon: '🧠' },
  { id: '2', type: 'break', label: '休息', duration: 15, startHour: 10.5, color: '#10B981', icon: '☕' },
  { id: '3', type: 'pomodoro', label: '番茄钟 ×2', duration: 50, startHour: 11, color: '#EF4444', icon: '🍅' },
  { id: '4', type: 'break', label: '午餐', duration: 60, startHour: 12, color: '#10B981', icon: '🍽️' },
  { id: '5', type: 'meeting', label: '团队会议', duration: 60, startHour: 13, color: '#3B82F6', icon: '👥' },
  { id: '6', type: 'deep-work', label: '深度工作', duration: 120, startHour: 14, color: '#8B5CF6', icon: '🧠' },
  { id: '7', type: 'admin', label: '邮件/行政', duration: 30, startHour: 16, color: '#6B7280', icon: '📧' },
  { id: '8', type: 'pomodoro', label: '番茄钟 ×2', duration: 50, startHour: 16.5, color: '#EF4444', icon: '🍅' },
  { id: '9', type: 'break', label: '休息', duration: 10, startHour: 17.5, color: '#10B981', icon: '🧘' },
];

const POMODORO_DURATION = 25 * 60; // 25分钟（秒）
const BREAK_DURATION = 5 * 60; // 5分钟（秒）

// ==================== 子组件 ====================

// 标题组件
const Title: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const opacity = spring({
    frame,
    fps,
    config: { damping: 200 },
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: 50,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity,
      }}
    >
      <div style={{ fontSize: 68, fontWeight: 'bold', color: 'white', marginBottom: 10 }}>
        时间盒工作法 ⏱️
      </div>
      <div style={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.6)' }}>
        Timeboxing • 将时间划分为专注块
      </div>
    </div>
  );
};

// 时间轴组件
const Timeline: React.FC<{
  frame: number;
  fps: number;
  blocks: TimeBlock[];
}> = ({ frame, fps, blocks }) => {
  const timelineProgress = spring({
    frame: frame - 30,
    fps,
    config: { damping: 200 },
  });

  const timelineWidth = 1600;
  const startX = 160;
  const endX = startX + timelineWidth;

  // 时间范围：9:00 - 18:00
  const startHour = 9;
  const endHour = 18;
  const totalHours = endHour - startHour;

  const hourToX = (hour: number) => {
    return startX + ((hour - startHour) / totalHours) * timelineWidth;
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 200,
        left: 0,
        right: 0,
        opacity: timelineProgress,
      }}
    >
      {/* 时间轴线 */}
      <div
        style={{
          position: 'absolute',
          left: startX,
          top: 60,
          width: timelineWidth,
          height: 4,
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
        }}
      />

      {/* 时间刻度 */}
      {Array.from({ length: totalHours + 1 }).map((_, i) => {
        const hour = startHour + i;
        const x = hourToX(hour);
        const tickProgress = spring({
          frame: frame - 40 - i * 3,
          fps,
          config: { damping: 200 },
        });

        return (
          <div
            key={hour}
            style={{
              position: 'absolute',
              left: x,
              top: 40,
              opacity: tickProgress,
              transform: 'translateX(-50%)',
            }}
          >
            <div
              style={{
                width: 3,
                height: 40,
                background: 'rgba(255, 255, 255, 0.4)',
                margin: '0 auto',
              }}
            />
            <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.6)', marginTop: 8, textAlign: 'center' }}>
              {hour}:00
            </div>
          </div>
        );
      })}

      {/* 时间块 */}
      {blocks.map((block, index) => {
        const blockProgress = spring({
          frame: frame - 60 - index * 10,
          fps,
          config: { damping: 200 },
        });

        const blockStartX = hourToX(block.startHour);
        const blockEndX = hourToX(block.startHour + block.duration / 60);
        const rawWidth = blockEndX - blockStartX;
        const gap = 6;
        const blockLeft = blockStartX + gap / 2;
        const blockWidth = rawWidth - gap;
        const isNarrow = blockWidth < 55;
        const isWide = blockWidth >= 240;

        return (
          <div
            key={block.id}
            style={{
              position: 'absolute',
              left: blockLeft,
              top: 130,
              width: blockWidth,
              height: 76,
              background: `${block.color}30`,
              border: `2px solid ${block.color}`,
              borderRadius: 10,
              opacity: blockProgress,
              transform: `scaleY(${blockProgress})`,
              transformOrigin: 'top',
              display: 'flex',
              flexDirection: isWide ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: isWide ? 'space-between' : 'center',
              padding: isWide ? '0 14px' : '0 4px',
              overflow: 'hidden',
              boxSizing: 'border-box',
              gap: isNarrow ? 0 : 2,
            }}
          >
            {isNarrow ? (
              <div style={{ fontSize: 24 }}>{block.icon}</div>
            ) : isWide ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden', flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 28, flexShrink: 0 }}>{block.icon}</div>
                  <div style={{
                    fontSize: 22,
                    fontWeight: 'bold',
                    color: block.color,
                    whiteSpace: 'nowrap',
                  }}>
                    {block.label}
                  </div>
                </div>
                <div style={{
                  fontSize: 18,
                  color: 'rgba(255,255,255,0.6)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {block.duration}分钟
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 22, lineHeight: 1 }}>{block.icon}</div>
                <div style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  color: block.color,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}>
                  {block.label}
                </div>
                {blockWidth >= 100 && (
                  <div style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.5)',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}>
                    {block.duration}分钟
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

// 番茄钟倒计时组件
const PomodoroTimer: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const timerProgress = spring({
    frame: frame - 120,
    fps,
    config: { damping: 200 },
  });

  const cycleFrame = frame % 180;
  const isWork = Math.floor(frame / 180) % 2 === 0;
  const currentDuration = isWork ? POMODORO_DURATION : BREAK_DURATION;
  const elapsed = (cycleFrame / 180) * currentDuration;
  const remaining = Math.max(0, currentDuration - elapsed);

  const minutes = Math.floor(remaining / 60);
  const seconds = Math.floor(remaining % 60);

  const progress = 1 - elapsed / currentDuration;
  const circumference = 2 * Math.PI * 90;
  const strokeDashoffset = circumference * (1 - progress);

  const color = isWork ? '#EF4444' : '#10B981';

  return (
    <div
      style={{
        flex: 1,
        opacity: timerProgress,
        transform: `scale(${timerProgress})`,
        background: 'rgba(239, 68, 68, 0.08)',
        border: '2px solid rgba(239, 68, 68, 0.3)',
        borderRadius: 20,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 'bold', color: 'white', marginBottom: 16 }}>
        🍅 番茄钟计时器
      </div>

      <div style={{ position: 'relative', width: 200, height: 200 }}>
        <svg style={{ position: 'absolute', top: 0, left: 0, width: 200, height: 200 }}>
          <circle cx={100} cy={100} r={90} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={10} />
          <circle
            cx={100} cy={100} r={90} fill="none" stroke={color} strokeWidth={10}
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
          />
        </svg>

        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 32, marginBottom: -4 }}>{isWork ? '🍅' : '☕'}</div>
          <div style={{ fontSize: 40, fontWeight: 'bold', color }}>
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 21, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>
            {isWork ? '专注中' : '休息中'}
          </div>
        </div>
      </div>
    </div>
  );
};

// 深度工作说明卡片
const DeepWorkCard: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const cardProgress = spring({
    frame: frame - 150,
    fps,
    config: { damping: 200 },
  });

  const tips = [
    { icon: '🚫', title: '关闭通知', desc: '屏蔽所有干扰' },
    { icon: '🎯', title: '单一任务', desc: '专注一件事' },
    { icon: '⏰', title: '固定时段', desc: '90分钟深度工作' },
    { icon: '📝', title: '事先计划', desc: '明确目标再开始' },
  ];

  return (
    <div
      style={{
        flex: 1,
        opacity: cardProgress,
        transform: `translateX(${interpolate(cardProgress, [0, 1], [50, 0])}px)`,
        background: 'rgba(139, 92, 246, 0.15)',
        border: '2px solid #8B5CF6',
        borderRadius: 20,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 'bold', color: '#8B5CF6', marginBottom: 16 }}>
        🧠 深度工作要点
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
        {tips.map((tip, index) => {
          const tipProgress = spring({
            frame: frame - 160 - index * 8,
            fps,
            config: { damping: 200 },
          });

          return (
            <div
              key={tip.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                opacity: tipProgress,
                transform: `translateX(${interpolate(tipProgress, [0, 1], [-20, 0])}px)`,
              }}
            >
              <div style={{ fontSize: 26 }}>{tip.icon}</div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: 'white' }}>{tip.title}</div>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.6)' }}>{tip.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 效率统计组件
const EfficiencyStats: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const statsProgress = spring({
    frame: frame - 200,
    fps,
    config: { damping: 200 },
  });

  const stats = [
    { label: '深度工作', value: 210, unit: '分钟', color: '#8B5CF6', percentage: 47 },
    { label: '番茄钟', value: 4, unit: '次', color: '#EF4444', percentage: 22 },
    { label: '会议', value: 60, unit: '分钟', color: '#3B82F6', percentage: 13 },
    { label: '休息', value: 85, unit: '分钟', color: '#10B981', percentage: 18 },
  ];

  return (
    <div
      style={{
        flex: 1,
        opacity: statsProgress,
        transform: `translateY(${interpolate(statsProgress, [0, 1], [30, 0])}px)`,
        background: 'rgba(59, 130, 246, 0.08)',
        border: '2px solid rgba(59, 130, 246, 0.3)',
        borderRadius: 20,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 'bold', color: 'white', marginBottom: 16 }}>
        📊 今日时间分配
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        {stats.map((stat, index) => {
          const statProgress = spring({
            frame: frame - 210 - index * 8,
            fps,
            config: { damping: 200 },
          });

          const barWidth = interpolate(statProgress, [0, 1], [0, stat.percentage]);

          return (
            <div key={stat.label} style={{ opacity: statProgress }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: stat.color }} />
                  <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.7)' }}>{stat.label}</div>
                </div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: stat.color }}>
                  {stat.value}
                  <span style={{ fontSize: 20, color: 'rgba(255,255,255,0.5)', marginLeft: 4 }}>{stat.unit}</span>
                </div>
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${barWidth}%`, height: '100%', background: stat.color, borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

export const TimeboxingAnimation: React.FC<TimeboxingAnimationProps> = ({
  backgroundColor = '#0A0E27',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill style={{ background: backgroundColor }}>
      <Title frame={frame} fps={fps} />
      <Timeline frame={frame} fps={fps} blocks={TIME_BLOCKS} />
      <div
        style={{
          position: 'absolute',
          top: 480,
          left: 80,
          right: 80,
          bottom: 40,
          display: 'flex',
          gap: 24,
        }}
      >
        <PomodoroTimer frame={frame} fps={fps} />
        <EfficiencyStats frame={frame} fps={fps} />
        <DeepWorkCard frame={frame} fps={fps} />
      </div>
    </AbsoluteFill>
  );
};
